import { notify, confirmBottom, checkedFetch } from './feedback';
import BottomFeedback from './BottomFeedback';
import WalletLedgerNotice from './WalletLedgerNotice';
import WalletSyncStatus from './WalletSyncStatus';
import Debt from './Debt';
// /src/App.jsx (React frontend)
import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import SignatureCanvas from 'react-signature-canvas'
import html2pdf from 'html2pdf.js'
import CryptoJS from 'crypto-js'
import './App.css'
import FinancialReports from './FinancialReports'
import BOM from './BOM'

const API_BASE = 'https://true-interactivity-internal-services-worker.badamasiabdulmalik.workers.dev'


function ExpensesList() {
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [deletingId, setDeletingId] = useState(null)

  useEffect(() => {
    async function fetchExpenses() {
      try {
        const res = await fetch(`${API_BASE}/v1/expenses`)
        if (!res.ok) throw new Error('Failed to fetch expenses')
        const data = await res.json()
        setExpenses(Array.isArray(data) ? data : [])
      } catch (err) {
        setError(err.message || 'Unexpected error')
      }
      setLoading(false)
    }
    fetchExpenses()
  }, [])

  if (loading) return <p>Loading expenses...</p>
  if (error) return <p className="error-message">{error}</p>

  async function deletePendingExpense(expense) {
    if (expense.status !== 'Pending') return
    if (!(await confirmBottom(`Delete pending expense "${expense.item_name}"? This will permanently remove it.`))) return
    setDeletingId(expense.id)
    setError('')
    try {
      const res = await fetch(`${API_BASE}/v1/expenses/${expense.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Delete failed')
      setExpenses(current => current.filter(exp => exp.id !== expense.id))
      notify(`Expense ${expense.id} deleted.`, 'success')
    } catch (err) {
      notify(err.message || 'Failed to delete pending expense', 'error')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div>
      <h1>Expenses</h1>
      <div className="table-container" tabIndex={0} role="region" aria-label="Scrollable table"><table className="expenses-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Expense Category</th>
            <th>Subcategory</th>
            <th>Item</th>
            <th>Amount</th>
            <th>Currency</th>
            <th>Account Name</th>
            <th>Bank Name</th>
            <th>Account Number</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Description</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((exp) => (
            <tr key={exp.id}>
              <td>{exp.id}</td>
              <td>{exp.expense_category}</td>
              <td>{exp.expense_subcategory || '—'}</td>
              <td>{exp.item_name}</td>
              <td>{exp.amount}</td>
              <td>{exp.currency}</td>
              <td>{exp.account_name || 'N/A'}</td>
              <td>{exp.bank_name || 'N/A'}</td>
              <td>{exp.account_number || 'N/A'}</td>
              <td>{exp.status}</td>
              <td>{exp.created_at}</td>
              <td>{exp.description}</td>
              <td>
                {exp.status === 'Pending' ? (
                  <button
                    type="button"
                    onClick={() => deletePendingExpense(exp)}
                    disabled={deletingId === exp.id}
                    style={{ background: '#dc3545' }}
                  >
                    {deletingId === exp.id ? 'Deleting...' : 'Delete'}
                  </button>
                ) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  )
}

function ApprovalsList() {
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function fetchExpenses() {
      try {
        const res = await fetch(`${API_BASE}/v1/expenses`)
        if (!res.ok) throw new Error('Failed to fetch expenses')
        const data = await res.json()
        // ✅ Show only unapproved expenses
        // Exclude: approved, failed transfers, pending-verification transfers (those belong in Pending Transfers page)
        setExpenses(data.filter((exp) =>
          exp.status !== 'Approved' &&
          exp.status !== 'Failed' &&
          exp.status !== 'Pending Verification' &&
          !exp.transfer_id  // transfer-backed expenses are handled in Pending Transfers
        ))
      } catch (err) {
        setError(err.message || 'Unexpected error')
      }
      setLoading(false)
    }
    fetchExpenses()
  }, [])

  async function confirmExpense(id) {
    try {
      await checkedFetch(`${API_BASE}/v1/expenses/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Approved', approved_by: 'Abdulmalik Badamasi' }),
      })
      // Remove from pending list after approval
      setExpenses(expenses.filter((exp) => exp.id !== id))
      notify(`Expense ${id} approved.`, 'success')
    } catch (err) {
      notify(err.message || 'Could not approve the expense.', 'error')
    }
  }

  if (loading) return <p>Loading approvals...</p>
  if (error) return <p className="error-message">{error}</p>

  return (
    <div>
      <h1>Pending Approvals</h1>
      <div className="table-container" tabIndex={0} role="region" aria-label="Scrollable table"><table className="expenses-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Expense Category</th>
            <th>Subcategory</th>
            <th>Item</th>
            <th>Amount</th>
            <th>Currency</th>
            <th>Account Name</th>
            <th>Bank Name</th>
            <th>Account Number</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Description</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {expenses.map((exp) => (
            <tr key={exp.id}>
              <td>{exp.id}</td>
              <td>{exp.expense_category}</td>
              <td>{exp.expense_subcategory || '—'}</td>
              <td>{exp.item_name}</td>
              <td>{exp.amount}</td>
              <td>{exp.currency}</td>
              <td>{exp.account_name || 'N/A'}</td>
              <td>{exp.bank_name || 'N/A'}</td>
              <td>{exp.account_number || 'N/A'}</td>
              <td>{exp.status}</td>
              <td>{exp.created_at}</td>
              <td>{exp.description}</td>
              <td>
                <button onClick={() => confirmExpense(exp.id)}>Confirm</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div>
    </div>
  )
}

function IncomeDetails() {
  const [activeTab, setActiveTab] = useState('local')

  return (
    <div className="income-page">
      <h1>Wallet Income</h1>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
        Saved income is stored in this app’s database and includes manual entries and imported Flutterwave credits. The Flutterwave Ledger tab reads the live provider statement.
      </p>

      {/* Tab Switcher */}
      <div className="responsive-row" style={{ display: 'flex', gap: '0', marginBottom: '25px', borderBottom: '2px solid #eee' }}>
        <button onClick={() => setActiveTab('local')} style={{
          padding: '12px 24px', fontWeight: '600', fontSize: '14px',
          background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: activeTab === 'local' ? '3px solid #28a745' : '3px solid transparent',
          color: activeTab === 'local' ? '#28a745' : '#888'
        }}>📋 Saved Income</button>
        <button onClick={() => setActiveTab('ledger')} style={{
          padding: '12px 24px', fontWeight: '600', fontSize: '14px',
          background: 'none', border: 'none', cursor: 'pointer',
          borderBottom: activeTab === 'ledger' ? '3px solid #0070f3' : '3px solid transparent',
          color: activeTab === 'ledger' ? '#0070f3' : '#888'
        }}>🏦 Flutterwave Ledger</button>
      </div>

      {activeTab === 'local' && <LocalIncomeTab />}
      {activeTab === 'ledger' && <FlutterwaveLedgerTab />}
    </div>
  )
}

function LocalIncomeTab() {
  const [incomes, setIncomes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ amount: '', source: '', category: '', description: '', logged_by: 'System' })
  const [submitting, setSubmitting] = useState(false)

  const fetchIncomes = async () => {
    setLoading(true)
    setError('')
    try {
      const resp = await fetch(`${API_BASE}/v1/incomes`)
      const data = await resp.json()
      setIncomes(Array.isArray(data) ? data : [])
    } catch (err) {
      setError(err.message || 'Failed to load incomes')
    }
    setLoading(false)
  }

  const handleAddIncome = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await checkedFetch(`${API_BASE}/v1/incomes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      })
      notify('Wallet income saved.', 'success');
      setShowForm(false)
      setForm({ amount: '', source: '', category: '', description: '', logged_by: 'System' })
      fetchIncomes()
    } catch (err) {
      setError(err.message)
    }
    setSubmitting(false)
  }

  useEffect(() => {
    fetchIncomes();
    const refresh = () => fetchIncomes();
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, [])

  const localEntries = incomes.filter(i => !String(i.id).startsWith('flw_'))
  const syncedEntries = incomes.filter(i => String(i.id).startsWith('flw_'))
  const totalIncome = incomes.reduce((sum, i) => sum + Number(i.amount), 0)

  return (
    <div>
      {/* Summary */}
      <div className="responsive-row" style={{ display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ padding: '15px 25px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Income (All)</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>₦{totalIncome.toLocaleString()}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Manual Entries</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#0284c7' }}>{localEntries.length}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Synced from Flutterwave</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ca8a04' }}>{syncedEntries.length}</div>
        </div>
      </div>

      {/* Add Income */}
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => setShowForm(!showForm)} style={{
          padding: '10px 20px', background: showForm ? '#dc3545' : '#28a745', color: 'white',
          border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
        }}>{showForm ? '✕ Cancel' : '+ Record Income'}</button>
      </div>

      {showForm && (
        <form className="income-entry-form" onSubmit={handleAddIncome} style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '15px',
          background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', marginBottom: '20px'
        }}>
          <input type="number" placeholder="Amount" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }} />
          <input type="text" placeholder="Source (e.g., Client Name)" value={form.source}
            onChange={e => setForm({ ...form, source: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }} />
          <input type="text" placeholder="Category" value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }} />
          <input type="text" placeholder="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }} />
          <button type="submit" disabled={submitting} style={{
            gridColumn: '1 / -1', padding: '10px', background: '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
          }}>{submitting ? '⏳ Saving...' : '💾 Save Income'}</button>
        </form>
      )}

      {error && <p className="error-message">{error}</p>}

      {loading ? <p>Loading income records...</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead><tr>
              <th>Date</th><th>Source</th><th>Category</th><th>Amount</th><th>Origin</th><th>Description</th>
            </tr></thead>
            <tbody>
              {incomes.length > 0 ? incomes.map((inc) => {
                const isSynced = String(inc.id).startsWith('flw_')
                return (
                  <tr key={inc.id} style={{ backgroundColor: isSynced ? '#fefce8' : '#fafffa' }}>
                    <td>{new Date(inc.date).toLocaleString()}</td>
                    <td style={{ fontSize: '12px', maxWidth: '250px', wordWrap: 'break-word' }}>{inc.source}</td>
                    <td>{inc.category}</td>
                    <td style={{ color: '#28a745', fontWeight: 'bold' }}>+₦{Number(inc.amount).toLocaleString()}</td>
                    <td>
                      <span style={{
                        background: isSynced ? '#fde68a' : '#d4edda',
                        color: isSynced ? '#92400e' : '#155724',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
                      }}>{isSynced ? '🔄 Flutterwave' : '✍️ Manual'}</span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#555' }}>{inc.description}</td>
                  </tr>
                )
              }) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No income records found. Click "+ Record Income" or sync from Flutterwave Ledger tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function FlutterwaveLedgerTab() {
  const [statement, setStatement] = useState([])
  const [savedFallback, setSavedFallback] = useState(false);
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState(null)

  const fetchIncomes = async (pageNum = 1, append = false) => {
    setLoading(true)
    setError('')
    try {
      const today = new Date().toISOString().split('T')[0]
      const finalEndDate = endDate > today ? today : endDate
      const url = `${API_BASE}/flw/statement?currency=NGN&from=${startDate}&to=${finalEndDate}&size=50&page=${pageNum}&type=C${append && savedFallback ? '&source=saved' : ''}`
      const resp = await fetch(url, { cache: 'no-store' })
      const data = await resp.json()

      if (data.status === 'success' && data.data) {
        setSavedFallback(data.source === 'saved');
        const credits = data.data.transactions || []
        if (append && !data.reset) {
          setStatement(prev => [...prev, ...credits])
        } else {
          setStatement(credits)
        }
        if (data.data.page_info) {
          setHasMore(data.data.page_info.current_page < data.data.page_info.total_pages)
          setPage(data.data.page_info.current_page)
        }
      } else {
        throw new Error(data.message || 'Failed to fetch incomes')
      }
    } catch (err) {
      if (append) {
        notify(err.message || 'Could not load more entries. Please retry.', 'error');
      } else {
      setError(err.message || 'Unexpected error')
      }
    }
    setLoading(false)
  }

  const loadMore = () => { fetchIncomes(page + 1, true) }

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)
    try {
      const resp = await fetch(`${API_BASE}/flw/sync-income`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: startDate, to: endDate })
      })
      const data = await resp.json()
      setSyncResult(data)
      if (data.success) window.dispatchEvent(new Event('wallet-synced'))
    } catch (err) {
      setSyncResult({ success: false, error: err.message })
    }
    setSyncing(false)
  }

  useEffect(() => { fetchIncomes() }, [])

  useEffect(() => {
    const refresh = () => fetchIncomes(1, false);
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, [startDate, endDate]);

  return (
    <div>
      <p style={{ color: '#555', fontSize: '13px', marginBottom: '15px' }}>
        Live view of incoming credits from Flutterwave. Use <strong>Sync to Local</strong> to save these entries to your local database so they appear in Reports.
      </p>

      <div className="responsive-row" style={{
        display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px',
        padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', flexWrap: 'wrap'
      }}>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
        </div>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }} />
        </div>
        <button onClick={() => fetchIncomes(1, false)} disabled={loading} style={{
          padding: '10px 20px', background: loading ? '#ccc' : '#28a745', color: 'white',
          border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600'
        }}>{loading ? '⏳ Updating...' : '🔍 Filter Income'}</button>
        <button onClick={handleSync} disabled={syncing} style={{
          padding: '10px 20px', background: syncing ? '#ccc' : '#0070f3', color: 'white',
          border: 'none', borderRadius: '4px', cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: '600'
        }}>{syncing ? '⏳ Syncing...' : '🔄 Save to App Database'}</button>
      </div>

      {syncResult && (
        <div style={{
          padding: '12px 15px', marginBottom: '15px', borderRadius: '6px',
          background: syncResult.success ? '#d4edda' : '#f8d7da',
          color: syncResult.success ? '#155724' : '#721c24',
          border: `1px solid ${syncResult.success ? '#c3e6cb' : '#f5c6cb'}`, fontSize: '13px'
        }}>
          {syncResult.success
            ? `✅ Sync complete! Fetched ${syncResult.total_fetched} entries — ${syncResult.synced} new synced, ${syncResult.skipped} already existed.`
            : `❌ Sync failed: ${syncResult.error}`}
        </div>
      )}

      <WalletLedgerNotice visible={savedFallback} />
      {loading && statement.length === 0 ? <p>Loading income entries...</p> : error ? <p className="error-message">{error}</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead><tr>
              <th>Date</th><th>Reference</th><th>Type</th><th>Amount</th><th>Balance After</th><th>Description / Remarks</th>
            </tr></thead>
            <tbody>
              {statement.length > 0 ? (
                <>
                  {statement.map((entry, idx) => (
                    <tr key={idx} style={{ backgroundColor: '#fafffa' }}>
                      <td>{new Date(entry.date).toLocaleString()}</td>
                      <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>{entry.reference}</td>
                      <td><span style={{ background: '#d4edda', color: '#155724', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>CREDIT</span></td>
                      <td style={{ color: '#28a745', fontWeight: 'bold' }}>+₦{Number(entry.amount).toLocaleString()}</td>
                      <td style={{ fontWeight: '500' }}>{entry.balance_after == null ? '—' : `₦${Number(entry.balance_after).toLocaleString()}`}</td>
                      <td style={{ fontSize: '12px', color: '#555' }}>{entry.remarks}</td>
                    </tr>
                  ))}
                  {hasMore && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        <button onClick={loadMore} disabled={loading} style={{
                          padding: '10px 30px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
                        }}>{loading ? 'Loading...' : '⬇️ Load More Entries'}</button>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No income entries found for this date range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function IncomingTransfers() {
  const [statement, setStatement] = useState([])
  const [savedFallback, setSavedFallback] = useState(false);
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]) // Default last 90 days
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0])

  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const fetchStatement = async (pageNum = 1, append = false) => {
    setLoading(true)
    setError('')
    try {
      const today = new Date().toISOString().split('T')[0]
      const finalEndDate = endDate > today ? today : endDate

      const url = `${API_BASE}/flw/statement?currency=NGN&from=${startDate}&to=${finalEndDate}&size=50&page=${pageNum}${append && savedFallback ? '&source=saved' : ''}`
      const resp = await fetch(url, { cache: 'no-store' })
      const data = await resp.json()

      if (data.status === 'success' && data.data) {
        setSavedFallback(data.source === 'saved');
        if (append && !data.reset) {
          setStatement(prev => [...prev, ...(data.data.transactions || [])])
        } else {
          setStatement(data.data.transactions || [])
        }

        if (data.data.page_info) {
          setHasMore(data.data.page_info.current_page < data.data.page_info.total_pages)
          setPage(data.data.page_info.current_page)
        }
      } else {
        throw new Error(data.message || 'Failed to fetch statement')
      }
    } catch (err) {
      if (append) {
        notify(err.message || 'Could not load more entries. Please retry.', 'error');
      } else {
      setError(err.message || 'Unexpected error')
      }
    }
    setLoading(false)
  }

  const loadMore = () => {
    fetchStatement(page + 1, true)
  }

  useEffect(() => {
    fetchStatement()
  }, [])

  useEffect(() => {
    const refresh = () => fetchStatement(1, false);
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, [startDate, endDate]);

  return (
    <div className="incoming-transfers-page">
      <h1>General Wallet Ledger</h1>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
        Live transaction history from Flutterwave showing all incoming (credits) and outgoing (debits) wallet entries.
      </p>

      {/* Date Filters */}
      <div className="responsive-row" style={{
        display: 'flex',
        gap: '15px',
        alignItems: 'flex-end',
        marginBottom: '25px',
        padding: '15px',
        background: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #eee'
      }}>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>From Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>To Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <button
          onClick={fetchStatement}
          disabled={loading}
          style={{
            padding: '10px 20px',
            background: loading ? '#ccc' : '#0070f3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: '600'
          }}
        >
          {loading ? '⏳ Updating...' : '🔍 Filter Statement'}
        </button>
      </div>

      <WalletLedgerNotice visible={savedFallback} />
      {loading ? (
        <p>Fetching ledger entries from Flutterwave...</p>
      ) : error ? (
        <p className="error-message">{error}</p>
      ) : (
        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Balance Before</th>
                <th>Balance After</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {statement.length > 0 ? (
                <>
                  {statement.map((entry, idx) => {
                    const type = String(entry.type).toUpperCase();
                    const isCredit = type === 'C' || type === 'CREDIT';
                    const isDebit = type === 'D' || type === 'DEBIT';

                    return (
                      <tr key={idx} style={{ backgroundColor: isDebit ? '#fffafa' : '#fafffa' }}>
                        <td>{new Date(entry.date).toLocaleString()}</td>
                        <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>{entry.reference}</td>
                        <td>
                          <span style={{
                            background: isCredit ? '#d4edda' : isDebit ? '#f8d7da' : '#eee',
                            color: isCredit ? '#155724' : isDebit ? '#721c24' : '#666',
                            padding: '2px 8px',
                            borderRadius: '10px',
                            fontSize: '11px',
                            fontWeight: 'bold'
                          }}>
                            {isCredit ? 'CREDIT' : isDebit ? 'DEBIT' : type}
                          </span>
                        </td>
                        <td style={{ color: isCredit ? '#28a745' : isDebit ? '#dc3545' : '#444', fontWeight: 'bold' }}>
                          {isCredit ? '+' : isDebit ? '-' : ''}₦{Number(entry.amount).toLocaleString()}
                        </td>
                        <td style={{ color: '#888', fontSize: '13px' }}>{entry.balance_before == null ? '—' : `₦${Number(entry.balance_before).toLocaleString()}`}</td>
                        <td style={{ fontWeight: '500', color: '#111' }}>{entry.balance_after == null ? '—' : `₦${Number(entry.balance_after).toLocaleString()}`}</td>
                        <td style={{ fontSize: '12px', color: '#555', maxWidth: '300px', wordWrap: 'break-word' }}>{entry.remarks}</td>
                      </tr>
                    );
                  })}
                  {hasMore && (
                    <tr>
                      <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                        <button
                          onClick={loadMore}
                          disabled={loading}
                          style={{ padding: '8px 20px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                          {loading ? 'Loading...' : '⬇️ Load More Entries'}
                        </button>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No ledger entries found for this range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function Sidebar({ selected, setSelected }) {
    const [query, setQuery] = useState('');
    const options = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'make_payment', label: 'Make Payment' },
    { id: 'add_expenses', label: 'Add Expenses' },
    { id: 'expenses', label: 'Expenses' },
        { id: 'debt', label: 'Debt' },
    { id: 'pending_transfers', label: 'Pending Transfers' },
    { id: 'income', label: 'Wallet Income' },
    { id: 'wallet_expense', label: 'Wallet Expense' },
    { id: 'incoming_transfers', label: 'Wallet Ledger' },
    { id: 'salary', label: 'Salary' },
    { id: 'approvals', label: 'Approvals' },
    { id: 'reports', label: 'Financial Reports' },
    { id: 'contracts', label: 'Contracts' },
    { id: 'bom', label: 'BOM' },
  ];

    const normalizedQuery = query.trim().toLowerCase();
    const matches = options.filter(({ label }) => label.toLowerCase().includes(normalizedQuery));

    return (
        <nav className="sidebar" aria-label="Services">
            <h2 className="sidebar-title">Menu</h2>
            <div className="mobile-menu-search">
                <label htmlFor="menu-search">Search tabs</label>
                <div className="menu-search-controls">
                    <input
                        id="menu-search"
                        type="search"
                        placeholder="Search tabs, e.g. customer"
                        value={query}
                        onChange={event => setQuery(event.target.value)}
                        onKeyDown={event => { if (event.key === 'Escape') setQuery(''); }}
                        aria-controls="service-menu"
                    />
                    {query && <button type="button" onClick={() => setQuery('')}>Clear</button>}
                </div>
                {normalizedQuery && <p role="status">{matches.length ? `${matches.length} matching tabs` : 'No matching tabs. Try another name.'}</p>}
            </div>
            <ul id="service-menu" className={`sidebar-list ${normalizedQuery ? 'is-searching' : ''}`}>
                {options.map(({ id, label }) => (
                    <li key={id} className={matches.some(option => option.id === id) ? '' : 'menu-nonmatch'}>
                        <button
                            type="button"
                            className={`sidebar-item ${selected === id ? 'active' : ''}`}
                            aria-current={selected === id ? 'page' : undefined}
                            onClick={() => { setSelected(id); setQuery(''); }}
                        >
                            {label}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
}

function Card({ title, children, onClick }) {
  return (
    <div className="card" onClick={onClick}>
      <h2>{title}</h2>
      {children}
    </div>
  )
}

function AddExpenseForm() {
  const [form, setForm] = useState({
    expense_category: '',  // Changed from project_id
    expense_subcategory: '',
    project_id: '',
    item_name: '',
    description: '',
    amount: '',
    currency: 'NGN',
    category: '',
    payment_method: '',
    paid_to: '',
    bank_name: '',
    account_number: '',
    bank_code: '',
    approved_by: '',
    approved_at: '',
    created_by: '',
    status: 'Pending',
    notes: '',
    account_name: '', // Add this field
  })

  const [loading, setLoading] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const [banks, setBanks] = useState([])
  const [categories, setCategories] = useState([]) // 1. Add categories state
  const [subcategories, setSubcategories] = useState([])
  const [bankSearch, setBankSearch] = useState('')
  const [showBankDropdown, setShowBankDropdown] = useState(false)
  const [isCustomCategory, setIsCustomCategory] = useState(false) // 2. Add custom state
  const [isCustomSubcategory, setIsCustomSubcategory] = useState(false)

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch Banks
        const banksRes = await fetch(`${API_BASE}/flw/banks/NG`, {
          headers: {
            'Content-Type': 'application/json'
          }
        })
        if (banksRes.ok) {
          const banksData = await banksRes.json()
          setBanks(banksData.data || [])
        }

        // Fetch Categories
        const catRes = await fetch(`${API_BASE}/v1/expense-categories`)
        if (catRes.ok) {
          const catData = await catRes.json()
          setCategories(catData || [])
        }

        // Fetch Subcategories
        const subRes = await fetch(`${API_BASE}/v1/expense-subcategories`)
        if (subRes.ok) {
          const subData = await subRes.json()
          setSubcategories(subData || [])
        }
      } catch (err) {
        console.error('Fetch error:', err)
        // Fallback
      }
    }
    fetchData()
  }, [])

  const filteredBanks = banks.filter(bank =>
    bank.name.toLowerCase().includes(bankSearch.toLowerCase())
  )

  const verifyAccount = async (accountNumber, bankCode) => {
    if (!bankCode || !accountNumber || accountNumber.length !== 10) return

    try {
      setLoading(true)
      const response = await fetch(`${API_BASE}/flw/accounts/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${import.meta.env.VITE_FLUTTERWAVE_SECRET_KEY}`
        },
        body: JSON.stringify({
          account_number: accountNumber,
          account_bank: bankCode
        })
      })

      const data = await response.json()
      if (data.status === 'success') {
        setForm(prev => ({
          ...prev,
          account_name: data.data.account_name
        }))
        setErrorMsg('')
      } else {
        setErrorMsg('Account verification failed')
      }
    } catch (err) {
      console.error('Verification error:', err)
      setErrorMsg('Failed to verify account')
    } finally {
      setLoading(false)
    }
  }

  function handleChange(e) {
    const { name, value } = e.target
    setForm(prev => ({ ...prev, [name]: value }))

    // Auto-verify account number when it reaches 10 digits
    if (name === 'account_number' && value.length === 10) {
      const selectedBank = banks.find(bank => bank.name === form.bank_name)
      if (selectedBank) {
        verifyAccount(value, selectedBank.code)
      }
    }
  }

  const handleCategoryChange = (e) => {
    if (e.target.value === 'custom_option') {
      setIsCustomCategory(true);
      setForm(prev => ({ ...prev, expense_category: '' }));
    } else {
      setForm(prev => ({ ...prev, expense_category: e.target.value }));
    }
  };

  const handleCustomCategoryCancel = () => {
    setIsCustomCategory(false);
    setForm(prev => ({ ...prev, expense_category: '' }));
  };

  const handleSubcategoryChange = (e) => {
    if (e.target.value === 'custom_sub_option') {
      setIsCustomSubcategory(true)
      setForm(prev => ({ ...prev, expense_subcategory: '' }))
    } else {
      setForm(prev => ({ ...prev, expense_subcategory: e.target.value }))
    }
  }

  const handleCustomSubcategoryCancel = () => {
    setIsCustomSubcategory(false)
    setForm(prev => ({ ...prev, expense_subcategory: '' }))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setErrorMsg('')
    setSuccessMsg('')

    if (!form.expense_category || !form.item_name || !form.amount || !form.description) {
      setErrorMsg('Expense Category, Item Name, Amount and Description are required')
      return
    }

    setLoading(true)
    try {
      const expenseData = {
        expense_category: form.expense_category,
        expense_subcategory: form.expense_subcategory || null,
        project_id: form.project_id || 'General',
        item_name: form.item_name,
        description: form.description,
        amount: parseFloat(form.amount),
        currency: form.currency,
        payment_method: form.payment_method,
        paid_to: form.paid_to,
        bank_name: form.bank_name,
        account_number: form.account_number,
        bank_code: form.bank_code,
        created_by: form.created_by,
        status: 'Pending',
        notes: form.notes,
        account_name: form.account_name
      }

      const res = await fetch(`${API_BASE}/v1/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expenseData)
      })

      const data = await res.json()

      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit expense')
      }

      if (data.success) {
        const savedId = data.id ?? data.ids?.[0]
        setSuccessMsg(savedId ? `Expense added with ID: ${savedId}` : 'Expense added successfully')
        setForm({
          expense_category: '',
          expense_subcategory: '',
          project_id: '',
          item_name: '',
          description: '',
          amount: '',
          currency: 'NGN',
          payment_method: '',
          paid_to: '',
          bank_name: '',
          account_number: '',
          bank_code: '',
          created_by: '',
          status: 'Pending',
          notes: '',
          account_name: ''
        })
        setIsCustomCategory(false); // Reset
        setIsCustomSubcategory(false)
        setBankSearch('')
        setShowBankDropdown(false)
      } else {
        setErrorMsg('Failed to add expense')
      }
    } catch (error) {
      console.error('Submit error:', error)
      setErrorMsg(error.message || 'Unexpected error')
    }
    setLoading(false)
  }

  return (
    <>
      <h1>Add Expenses</h1>

      <form className="expense-form" onSubmit={handleSubmit} onChange={() => setSuccessMsg('')}>
        <label>Expense Category</label>
        {isCustomCategory ? (
          <div className="responsive-row" style={{ display: 'flex', gap: '10px' }}>
            <input
              name="expense_category"
              value={form.expense_category}
              onChange={handleChange}
              placeholder="Enter new category"
              required
            />
            <button
              type="button"
              onClick={handleCustomCategoryCancel}
              style={{ padding: '0 10px', whiteSpace: 'nowrap', width: 'auto', height: '40px', marginTop: '5px' }}
            >
              Back
            </button>
          </div>
        ) : (
          <select
            name="expense_category"
            value={form.expense_category}
            onChange={handleCategoryChange}
            required
          >
            <option value="">Select Category</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
            <option value="custom_option">Create New Category...</option>
          </select>
        )}

        <label>Expense Subcategory (Optional)</label>
        {isCustomSubcategory ? (
          <div className="responsive-row" style={{ display: 'flex', gap: '10px' }}>
            <input
              name="expense_subcategory"
              value={form.expense_subcategory}
              onChange={handleChange}
              placeholder="Enter new subcategory"
            />
            <button
              type="button"
              onClick={handleCustomSubcategoryCancel}
              style={{ padding: '0 10px', whiteSpace: 'nowrap', width: 'auto', height: '40px', marginTop: '5px' }}
            >
              Back
            </button>
          </div>
        ) : (
          <select
            name="expense_subcategory"
            value={form.expense_subcategory}
            onChange={handleSubcategoryChange}
          >
            <option value="">Select Subcategory</option>
            {subcategories.map((sub) => (
              <option key={sub.id} value={sub.name}>{sub.name}</option>
            ))}
            <option value="custom_sub_option">Create New Subcategory...</option>
          </select>
        )}

        <label>Project</label>
        <select
          name="project_id"
          value={form.project_id}
          onChange={handleChange}
        >
          <option value="General">General</option>
          <option value="Lili">LiLi 1</option>
        </select>

        <label>Item Name</label>
        <input
          name="item_name"
          value={form.item_name}
          onChange={handleChange}
          placeholder="Item Name"
          required
        />

        <label>Description</label>
        <textarea
          name="description"
          value={form.description}
          onChange={handleChange}
          placeholder="Description"
        />

        <label>Amount</label>
        <input
          name="amount"
          type="number"
          step="0.01"
          value={form.amount}
          onChange={handleChange}
          placeholder="Amount"
          required
        />

        <label>Currency</label>
        <select
          name="currency"
          value={form.currency}
          onChange={handleChange}
        >
          <option value="NGN">NGN</option>
          <option value="USD">USD</option>
        </select>

        <label>Payment Method</label>
        <select
          name="payment_method"
          value={form.payment_method}
          onChange={handleChange}
          required
        >
          <option value="">Select Payment Method</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="cash">Cash</option>
          <option value="cheque">Cheque</option>
        </select>

        <label>Paid To</label>
        <input
          name="paid_to"
          value={form.paid_to}
          onChange={handleChange}
          placeholder="Paid To"
        />

        <label>Bank Name (optional)</label>
        <div className="bank-search-container" style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="Search bank name..."
            value={bankSearch}
            onChange={(e) => {
              setBankSearch(e.target.value)
              setShowBankDropdown(true)
              setForm(prev => ({ ...prev, bank_name: '', bank_code: '', account_name: '' }))
            }}
            onFocus={() => setShowBankDropdown(true)}
            className="bank-search-input"
            autoComplete="off"
          />
          {showBankDropdown && filteredBanks.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              maxHeight: '220px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
            }}>
              {filteredBanks.map((bank, index) => (
                <button
                  key={`${bank.code}-${index}`}
                  type="button"
                  onClick={() => {
                    setForm(prev => ({
                      ...prev,
                      bank_name: bank.name,
                      bank_code: bank.code
                    }))
                    setBankSearch(bank.name)
                    setShowBankDropdown(false)
                    if (form.account_number && form.account_number.length === 10) {
                      verifyAccount(form.account_number, bank.code)
                    }
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '10px 12px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    border: 'none',
                    borderBottom: index < filteredBanks.length - 1 ? '1px solid #f0f0f0' : 'none',
                    backgroundColor: 'white',
                    color: '#333',
                    fontSize: '14px'
                  }}
                >
                  {bank.name}
                </button>
              ))}
            </div>
          )}
          {showBankDropdown && bankSearch && filteredBanks.length === 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '10px 12px',
              zIndex: 1000,
              color: '#666'
            }}>
              No matching banks found.
            </div>
          )}
          <input type="hidden" name="bank_name" value={form.bank_name} />
        </div>

        <label>Account Number (optional)</label>
        <div className="account-number-container">
          <input
            name="account_number"
            value={form.account_number}
            onChange={handleChange}
            placeholder="Enter 10-digit account number"
            pattern="[0-9]{10}"
            maxLength="10"
          />
          {loading && <span className="verify-status">Verifying...</span>}
          {form.account_name && (
            <div className="verified-account">
              <span className="verified-label">Account Name:</span>
              <span className="verified-name">{form.account_name}</span>
            </div>
          )}
        </div>

        <label>Created By</label>
        <select
          name="created_by"
          value={form.created_by}
          onChange={handleChange}
          required
        >
          <option value="">Select Creator</option>
          <option value="Abdulmalik">Abdulmalik</option>
        </select>

        <label>Notes</label>
        <textarea
          name="notes"
          value={form.notes}
          onChange={handleChange}
          placeholder="Notes"
        />

        <button type="submit" disabled={loading} style={successMsg ? { backgroundColor: '#198754', color: '#fff' } : undefined}>
          {loading ? 'Submitting...' : successMsg ? 'Expense Added' : 'Add Expense'}
        </button>
        {errorMsg && <div className="error-message" role="alert" style={{ marginTop: '12px' }}>{errorMsg}</div>}
                {successMsg && <div className="success-message" role="status" style={{ marginTop: '12px' }}>{successMsg}</div>}
      </form>
    </>
  )
}

function MakePayment() {
  const [banks, setBanks] = useState([])
  const [categories, setCategories] = useState([])
  const [subcategories, setSubcategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    bank_code: '',
    account_number: '',
    amount: '',
    description: '',
    recipient_name: '',
    // Unique product namespace lets the shared hub route without guessing.
    reference: `true-interactivity-transfer-${crypto.randomUUID()}`,
    expense_category: '',
    expense_subcategory: '',
    project_id: '',
    item_name: '',
    created_by: 'Abdulmalik'
  })
  const [bankSearch, setBankSearch] = useState('')
  const [showBankDropdown, setShowBankDropdown] = useState(false)
  const [isCustomCategory, setIsCustomCategory] = useState(false)
  const [isCustomSubcategory, setIsCustomSubcategory] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showFundingInfo, setShowFundingInfo] = useState(false)
  const [balance, setBalance] = useState(null)
  const [balanceLoading, setBalanceLoading] = useState(false)
  const [verifying, setVerifying] = useState(false) // Add verifying state
  const [showLowBalanceModal, setShowLowBalanceModal] = useState(false)
  const [useItemAsDescription, setUseItemAsDescription] = useState(false)
  const [copiedFundingAccount, setCopiedFundingAccount] = useState('')

  const copyFundingAccount = async (accountNumber) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(accountNumber)
      } else {
        const textArea = document.createElement('textarea')
        textArea.value = accountNumber
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
      }
      setCopiedFundingAccount(accountNumber)
      setTimeout(() => setCopiedFundingAccount(''), 1600)
    } catch (err) {
      setError('Could not copy account number. Please copy it manually.')
    }
  }

  const renderFundingAccountNumber = (accountNumber) => (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      marginLeft: '10px',
      flexWrap: 'wrap'
    }}>
      <button type="button" onClick={() => copyFundingAccount(accountNumber)} aria-label={`Copy account number ${accountNumber}`} title="Copy account number" style={{
        fontFamily: 'monospace',
        fontSize: '16px',
        color: '#007bff',
        fontWeight: 'bold',
          border: 0,
          background: 'transparent',
          padding: 0,
          cursor: 'pointer'
      }}>{accountNumber}</button>
      <button
        type="button"
        onClick={() => copyFundingAccount(accountNumber)}
        aria-label={`Copy account number ${accountNumber}`}
        title="Copy account number"
        style={{
          width: '24px',
          height: '24px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 0,
          borderRadius: '4px',
          backgroundColor: 'transparent',
          color: '#007bff',
          cursor: 'pointer',
          fontSize: '14px',
          padding: 0
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
      </button>
      {copiedFundingAccount === accountNumber && (
        <span style={{ color: '#198754', fontSize: '12px', fontWeight: '600' }}>Copied</span>
      )}
    </span>
  )

  const [verificationState, setVerificationState] = useState({
    show: false,
    message: '',
    countdown: 0
  })

  const handleItemNameChange = (e) => {
    const newVal = e.target.value
    setForm(prev => ({
      ...prev,
      item_name: newVal,
      description: useItemAsDescription ? newVal : prev.description
    }))
  }

  const handleUseDescriptionToggle = (e) => {
    const checked = e.target.checked
    setUseItemAsDescription(checked)
    if (checked) {
      setForm(prev => ({ ...prev, description: prev.item_name }))
    }
  }

  // Fetch banks and categories on component mount
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch Banks
        const bankResponse = await fetch(`${API_BASE}/flw/banks/NG`, {
          headers: {
            // 'Authorization': `Bearer ${import.meta.env.VITE_FLUTTERWAVE_SECRET_KEY}`,
            'Content-Type': 'application/json'
          }
        })

        if (!bankResponse.ok) {
          console.error('Response not OK:', await bankResponse.text())
          throw new Error('Failed to fetch banks')
        }

        const bankData = await bankResponse.json()
        console.log('Banks data:', bankData) // Debug log
        setBanks(bankData.data)

        // Fetch Categories
        const catRes = await fetch(`${API_BASE}/v1/expense-categories`)
        if (catRes.ok) {
          const catData = await catRes.json()
          setCategories(catData || [])
        }

        // Fetch Subcategories
        const subRes = await fetch(`${API_BASE}/v1/expense-subcategories`)
        if (subRes.ok) {
          const subData = await subRes.json()
          setSubcategories(subData || [])
        }

        // Fetch Balance
        const balRes = await fetch(`${API_BASE}/flw/balance`)
        if (balRes.ok) {
          const balData = await balRes.json()
          if (balData.status === 'success') {
            setBalance(balData.data)
          }
        }
      } catch (err) {
        console.error('Fetch error:', err) // Debug log
        setError('Failed to load banks list')
      }
    }
    fetchData()
  }, [])

  // Check balance threshold
  useEffect(() => {
    if (balance && balance.available_balance < 20000) {
      setShowLowBalanceModal(true)
    }
  }, [balance])

  const filteredBanks = banks
    .filter(bank => bank.name.toLowerCase().includes(bankSearch.toLowerCase()))
    .slice(0, 5) // Show only top 5 results


  const handleChange = (e) => {
    const { name, value } = e.target
    setForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleAmountChange = (e) => {
    const rawValue = e.target.value.replace(/,/g, '')
    if (!isNaN(rawValue) && /^\d*\.?\d*$/.test(rawValue)) {
      const parts = rawValue.split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
      setForm({ ...form, amount: parts.join('.') })
    }
  }

  const verifyAccount = async (accountNum, bankCode) => {
    // Use provided args or fallback to state
    const acc = accountNum || form.account_number
    const bank = bankCode || form.bank_code

    if (!bank) {
      setError('Please select a bank first')
      return
    }

    if (!acc || acc.length !== 10) {
      // Don't error while typing, just return
      return
    }

    try {
      setVerifying(true)
      const response = await fetch(`${API_BASE}/flw/accounts/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${import.meta.env.VITE_FLUTTERWAVE_SECRET_KEY}`
        },
        body: JSON.stringify({
          account_number: acc,
          account_bank: bank
        })
      })

      const data = await response.json()
      console.log('Verify response:', data) // Debug log

      if (data.status === 'success' && data.data?.account_name) {
        setForm(prev => ({
          ...prev,
          recipient_name: data.data.account_name
        }))
        setError('')
      } else {
        throw new Error(data.message || 'Account verification failed')
      }
    } catch (err) {
      console.error('Verification error:', err)
      setError(err.message || 'Failed to verify account')
    } finally {
      setVerifying(false)
    }
  }

  const fetchBalance = async () => {
    try {
      setBalanceLoading(true)
      const response = await fetch(`${API_BASE}/flw/balance`)
      const data = await response.json()

      if (data.status === 'success') {
        setBalance(data.data)
      } else {
        console.error('Failed to fetch balance:', data)
      }
    } catch (err) {
      console.error('Balance fetch error:', err)
    } finally {
      setBalanceLoading(false)
    }
  }

  const handleCategoryChange = (e) => {
    if (e.target.value === 'custom_option') {
      setIsCustomCategory(true)
      setForm(prev => ({ ...prev, expense_category: '' }))
    } else {
      setForm(prev => ({ ...prev, expense_category: e.target.value }))
    }
  }

  const handleCustomCategoryCancel = () => {
    setIsCustomCategory(false)
    setForm(prev => ({ ...prev, expense_category: '' }))
  }

  const handleSubcategoryChange = (e) => {
    if (e.target.value === 'custom_sub_option') {
      setIsCustomSubcategory(true)
      setForm(prev => ({ ...prev, expense_subcategory: '' }))
    } else {
      setForm(prev => ({ ...prev, expense_subcategory: e.target.value }))
    }
  }

  const handleCustomSubcategoryCancel = () => {
    setIsCustomSubcategory(false)
    setForm(prev => ({ ...prev, expense_subcategory: '' }))
  }

  const [showBalance, setShowBalance] = useState(false); // Collapsed by default

  const handleInitiatePayment = async (e) => {
    e.preventDefault()
    if (!form.recipient_name) {
      setError('Please verify account first')
      return
    }
    if (!form.amount) {
      setError('Please enter amount')
      return
    }
    setShowConfirmation(true)
  }

  const confirmPayment = async () => {
    setShowConfirmation(false)
    try {
      setLoading(true)
      const transferPayload = {
        account_bank: form.bank_code,
        account_number: form.account_number,
        amount: parseFloat(form.amount.replace(/,/g, '')),
        narration: form.description,
        currency: "NGN",
        reference: form.reference,
        beneficiary_name: form.recipient_name,
        // Removed callback_url
        meta: {
          sender: "True Interactivity Entertainment Network Ltd",
          sender_country: "NG"
          // Removed mobile_number as it's optional
        }
      }

      const response = await fetch(`${API_BASE}/flw/transfers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${import.meta.env.VITE_FLUTTERWAVE_SECRET_KEY}`
        },
        body: JSON.stringify(transferPayload)
      })

      const data = await response.json()
      console.log('Transfer response:', data) // Debug log

      // Check if API call succeeded
      // Check if API call succeeded
      if (data.status === 'success' && data.data) {
        let transferStatus = data.data.status // SUCCESSFUL, PENDING, FAILED
        const transferId = String(parseInt(data.data.id)) // Convert to integer string

        // Auto-save custom category if it doesn't exist
        if (form.expense_category && !categories.find(cat => cat.name === form.expense_category)) {
          try {
            await fetch(`${API_BASE}/v1/expense-categories`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ name: form.expense_category })
            })
          } catch (catError) {
            console.error('Failed to save custom category:', catError)
          }
        }

        // ---------------------------------------------------------
        // 1. SAVE TRANSACTION IMMEDIATELY (Prevents data loss on close)
        // ---------------------------------------------------------
        let initialExpenseStatus = 'Pending Verification'
        let initialApprovedBy = null
        let initialApprovedAt = null

        if (transferStatus === 'SUCCESSFUL') {
          initialExpenseStatus = 'Approved'
          initialApprovedBy = 'System (Payment)'
          initialApprovedAt = new Date().toISOString()
        } else if (transferStatus === 'FAILED') {
          initialExpenseStatus = 'Failed'
        }

        let savedExpenseId = null;
        try {
          const expenseData = {
            expense_category: form.expense_category,
            expense_subcategory: form.expense_subcategory || null,
            project_id: form.project_id || 'General',
            item_name: form.item_name,
            description: form.description,
            amount: parseFloat(form.amount.replace(/,/g, '')),
            currency: "NGN",
            payment_method: "bank_transfer",
            paid_to: form.recipient_name,
            bank_name: banks.find(b => b.code === form.bank_code)?.name || '',
            account_number: form.account_number,
            bank_code: form.bank_code,
            created_by: form.created_by,
            status: initialExpenseStatus,
            approved_by: initialApprovedBy,
            approved_at: initialApprovedAt,
            notes: `Payment Ref: ${form.reference}`,
            account_name: form.recipient_name,
            transfer_id: transferId,
            transfer_reference: form.reference,
            transfer_status: transferStatus
          }

          const res = await fetch(`${API_BASE}/v1/expenses`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(expenseData)
          })
          const resData = await res.json()
          if (resData.success && resData.ids) savedExpenseId = resData.ids[0]
        } catch (expError) {
          console.error('Failed to save initial expense record:', expError)
        }

        // ---------------------------------------------------------
        // 2. POLLING PROGRESS BAR (If needed)
        // ---------------------------------------------------------
        let isResolved = false
        if (transferStatus === 'PENDING' || transferStatus === 'NEW') {
          setVerificationState({ show: true, countdown: 15, message: 'Verifying transaction...', isCancelled: false })

          // Phase 1: 15s countdown, poll every 2s
          for (let i = 15; i > 0; i--) {
            // Check if user cancelled
            setVerificationState(prev => {
              if (prev.isCancelled) isResolved = true // Exit flag
              return { ...prev, countdown: i }
            })
            if (isResolved) break;

            if (i % 2 === 0) {
              try {
                const statData = await fetch(`${API_BASE}/flw/transfers/${transferId}`).then(r => r.json())
                if (statData.status === 'success' && statData.data) {
                  if (statData.data.status === 'SUCCESSFUL' || statData.data.status === 'FAILED') {
                    transferStatus = statData.data.status
                    isResolved = true
                    break
                  }
                }
              } catch (e) { console.error('Poll error:', e) }
            }
            await new Promise(r => setTimeout(r, 1000))
          }

          if (!isResolved) {
            setVerificationState(prev => ({ ...prev, show: true, countdown: 30, message: 'Verification is taking longer. Continuing to verify...' }))
            // Phase 2: 30s countdown, poll every 3s
            for (let i = 30; i > 0; i--) {
              // Check if user cancelled
              setVerificationState(prev => {
                if (prev.isCancelled) isResolved = true // Exit flag
                return { ...prev, countdown: i }
              })
              if (isResolved) break;

              if (i % 3 === 0) {
                try {
                  const statData = await fetch(`${API_BASE}/flw/transfers/${transferId}`).then(r => r.json())
                  if (statData.status === 'success' && statData.data) {
                    if (statData.data.status === 'SUCCESSFUL' || statData.data.status === 'FAILED') {
                      transferStatus = statData.data.status
                      isResolved = true
                      break
                    }
                  }
                } catch (e) { console.error('Poll error:', e) }
              }
              await new Promise(r => setTimeout(r, 1000))
            }
          }

          // If polling resolved the status, update the database!
          if (transferStatus === 'SUCCESSFUL' && savedExpenseId) {
            await fetch(`${API_BASE}/v1/expenses/${savedExpenseId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'Approved', transfer_status: 'SUCCESSFUL', approved_by: 'System (Verified)', approved_at: new Date().toISOString() })
            })
          } else if (transferStatus === 'FAILED' && savedExpenseId) {
            await fetch(`${API_BASE}/v1/expenses/${savedExpenseId}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'Failed', transfer_status: 'FAILED' })
            })
          }
        }

        // ---------------------------------------------------------
        // 3. SHOW FINAL ALERT MESSAGE
        // ---------------------------------------------------------
        let alertMessage = ''
        if (transferStatus === 'SUCCESSFUL') {
          alertMessage = '✅ Transfer completed successfully!'
        } else if (transferStatus === 'PENDING' || transferStatus === 'NEW') {
          alertMessage = '⏳ Transfer is pending verification. Check status later in "Pending Transfers" page.'
        } else if (transferStatus === 'FAILED') {
          alertMessage = `❌ Transfer failed. Check Pending/Failed Transfers page.`
        } else {
          alertMessage = `⚠️ Transfer status: ${transferStatus}. Please verify manually.`
        }

        if (transferStatus === 'SUCCESSFUL') {
          setVerificationState({
            show: true,
            countdown: 0,
            message: 'Transfer completed successfully',
            status: 'SUCCESSFUL',
            isCancelled: false,
            details: { amount: form.amount, recipient: form.recipient_name, reference: form.reference, transferId }
          })
        } else {
          setVerificationState({ show: false, countdown: 0, message: '' })
          notify(alertMessage)
        }

        // Form reset
        setForm({
          bank_code: '',
          account_number: '',
          amount: '',
          description: '',
          recipient_name: '',
          reference: `true-interactivity-transfer-${crypto.randomUUID()}`,
          expense_category: '',
          expense_subcategory: '',
          project_id: '',
          item_name: '',
          created_by: 'Abdulmalik'
        })
        setIsCustomCategory(false)
        setIsCustomSubcategory(false)
      } else {
        setError(data.message || 'Transfer failed')
      }
    } catch (err) {
      console.error('Transfer error:', err)
      setError('Transfer failed: ' + (err.message || 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="make-payment-container">
      <h1>Make Payment</h1>

      {/* Flutterwave Wallet Funding Info Button */}
      <div style={{ marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => setShowFundingInfo(!showFundingInfo)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#f5a623',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#e09612'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#f5a623'}
        >
          <span>💳</span>
          <span>Flutterwave Wallet Funding Info</span>
          <span>{showFundingInfo ? '▼' : '▶'}</span>
        </button>
      </div>

      {/* Account Balance (Always Visible, Toggleable) */}
      <div
        onClick={() => setShowBalance(!showBalance)}
        style={{
          backgroundColor: '#d4edda',
          border: '1px solid #28a745',
          borderRadius: '8px',
          marginBottom: '20px',
          overflow: 'hidden',
          cursor: 'pointer'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 15px',
            backgroundColor: '#d4edda'
          }}
        >
          <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <strong style={{ color: '#155724', fontSize: '15px' }}>💵 Account Balance</strong>
            <span style={{ fontSize: '12px', color: '#155724' }}>{showBalance ? '▼' : '▶'}</span>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); fetchBalance(); }}
            disabled={balanceLoading}
            style={{
              padding: '4px 10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: balanceLoading ? 'not-allowed' : 'pointer',
              fontSize: '11px',
              fontWeight: '600'
            }}
          >
            {balanceLoading ? '...' : 'Refresh'}
          </button>
        </div>

        {showBalance && balance && (
          <div style={{ padding: '0 15px 15px 15px', borderTop: '1px solid #c3e6cb' }}>
            <div style={{ display: 'grid', gap: '5px', marginTop: '10px' }}>
              <div>
                <span style={{ color: '#155724', fontSize: '12px' }}>Available Balance:</span>
                <div style={{
                  fontSize: '20px',
                  fontWeight: 'bold',
                  color: '#28a745',
                  fontFamily: 'monospace'
                }}>
                  ₦{Number(balance.available_balance || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#155724' }}>
                Ledger Balance: ₦{Number(balance.ledger_balance || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        )}
        {showBalance && !balance && (
          <div style={{ padding: '15px', textAlign: 'center', color: '#155724', fontSize: '13px' }}>
            Click "Refresh" to see balance
          </div>
        )}
      </div>

      {/* Funding Info Panel */}
      {showFundingInfo && (
        <div style={{
          backgroundColor: '#fff3cd',
          border: '2px solid #f5a623',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '30px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          {/* Account 1 */}
          <div style={{
            backgroundColor: '#ffffff',
            padding: '15px',
            borderRadius: '10px',
            marginBottom: '12px',
            border: '1px solid #dee2e6'
          }}>
            <h4 style={{ color: '#f5a623', marginTop: 0, fontSize: '16px' }}>
              🏦 Account 01
            </h4>
            <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
              <div>
                <strong style={{ color: '#495057' }}>Account Number:</strong>
                {renderFundingAccountNumber('9728967057')}
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Account Name:</strong>
                <span style={{ marginLeft: '10px', color: '#212529' }}>
                  True Interactivity Ltd
                </span>
              </div>
              <div>
                <strong style={{ color: '#495057' }}>Bank:</strong>
                <span style={{ marginLeft: '10px', color: '#212529' }}>Flutterwave MFB</span>
              </div>
            </div>
          </div>
        </div>
      )}




      <form onSubmit={handleInitiatePayment} className="payment-form">
        <div className="form-group">
          <label>Expense Category</label>
          {isCustomCategory ? (
            <div className="responsive-row" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                name="expense_category"
                value={form.expense_category}
                onChange={handleChange}
                placeholder="Enter new category name"
                required
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleCustomCategoryCancel}
                style={{ padding: '0 10px', whiteSpace: 'nowrap', width: 'auto' }}
                className="submit-btn"
              >
                Back
              </button>
            </div>
          ) : (
            <select
              name="expense_category"
              value={form.expense_category}
              onChange={handleCategoryChange}
              required
            >
              <option value="">Select Category</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.name}>{cat.name}</option>
              ))}
              <option value="custom_option">Create New Category...</option>
            </select>
          )}
        </div>

        <div className="form-group">
          <label>Expense Subcategory (Optional)</label>
          {isCustomSubcategory ? (
            <div className="responsive-row" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                name="expense_subcategory"
                value={form.expense_subcategory}
                onChange={handleChange}
                placeholder="Enter new subcategory name"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleCustomSubcategoryCancel}
                style={{ padding: '0 10px', whiteSpace: 'nowrap', width: 'auto' }}
                className="submit-btn"
              >
                Back
              </button>
            </div>
          ) : (
            <select
              name="expense_subcategory"
              value={form.expense_subcategory}
              onChange={handleSubcategoryChange}
            >
              <option value="">Select Subcategory</option>
              {subcategories.map((sub) => (
                <option key={sub.id} value={sub.name}>{sub.name}</option>
              ))}
              <option value="custom_sub_option">Create New Subcategory...</option>
            </select>
          )}
        </div>

        <div className="form-group">
          <label>Project</label>
          <select
            name="project_id"
            value={form.project_id}
            onChange={handleChange}
          >
            <option value="General">General</option>
            <option value="Lili">LiLi 1</option>
          </select>
        </div>

        <div className="form-group">
          <label>Item Name</label>
          <input
            name="item_name"
            value={form.item_name}
            onChange={handleItemNameChange}
            placeholder="Item Name"
            required
          />
        </div>



        <div className="form-group">
          <label>Account Number</label>
          <div className="account-verify-group">
            <input
              type="text"
              name="account_number"
              value={form.account_number}
              onChange={(e) => {
                // Only allow digits
                const value = e.target.value.replace(/\D/g, '')
                setForm(prev => ({
                  ...prev,
                  account_number: value
                }))

                // Auto-verify when length is 10
                if (value.length === 10 && form.bank_code) {
                  verifyAccount(value, form.bank_code)
                }
              }}
              inputMode="numeric"
              maxLength="10"
              required
              placeholder="Enter 10-digit account number"
            />
          </div>
          {verifying && <span className="verify-status">Verifying...</span>}
        </div>

        {form.recipient_name && (
          <div className="verified-account">
            <span className="verified-label">Account Name:</span>
            <span className="verified-name">{form.recipient_name}</span>
          </div>
        )}



        <div className="form-group">
          <label>Select Bank</label>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search Bank"
              value={bankSearch}
              onChange={(e) => {
                setBankSearch(e.target.value)
                setShowBankDropdown(true) // Show dropdown when typing
                // Clear selection when typing
                if (e.target.value) {
                  setForm(prev => ({ ...prev, bank_code: '', recipient_name: '' }))
                }
              }}
              onFocus={() => {
                setBankSearch('')
                setShowBankDropdown(true)
              }}
              className="bank-search-input"
              style={{ width: '100%' }}
              autoComplete="off"
            />

            {/* Autocomplete dropdown */}
            {showBankDropdown && bankSearch && filteredBanks.length > 0 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}>
                {filteredBanks.map((bank, index) => (
                  <div
                    key={`${bank.code}-${index}`}
                    onClick={() => {
                      setForm(prev => ({ ...prev, bank_code: bank.code }))
                      setBankSearch(bank.name)
                      setShowBankDropdown(false) // Hide dropdown after selection
                      if (form.account_number && form.account_number.length === 10) {
                        verifyAccount(form.account_number, bank.code)
                      }
                    }}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: index < filteredBanks.length - 1 ? '1px solid #f0f0f0' : 'none',
                      fontSize: '14px',
                      backgroundColor: 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {bank.name}
                  </div>
                ))}
              </div>
            )}

            {/* Hidden select for form validation */}
            <select
              name="bank_code"
              value={form.bank_code}
              onChange={(e) => {
                handleChange(e)
                if (form.account_number && form.account_number.length === 10 && e.target.value) {
                  verifyAccount(form.account_number, e.target.value)
                }
              }}
              required
              style={{ position: 'absolute', left: 0, top: 0, width: '1px', height: '1px', padding: 0, opacity: 0, pointerEvents: 'none' }}
            >
              <option value="">Select a bank</option>
              {banks.map((bank, index) => (
                <option key={`${bank.code}-${index}`} value={bank.code}>{bank.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Amount (NGN)</label>
          <input
            type="text"
            name="amount"
            value={form.amount}
            onChange={handleAmountChange}
            onFocus={() => {
              // Re-verify account when amount field is focused
              if (form.bank_code && form.account_number.length === 10 && !form.recipient_name) {
                verifyAccount(form.account_number, form.bank_code)
              }
            }}
            required
            placeholder="Enter amount"
          />
        </div>

        <div className="form-group">
          <label>Transfer Initiated By:</label>
          <select
            name="created_by"
            value={form.created_by}
            onChange={handleChange}
            required
          >
            <option value="Abdulmalik">Abdulmalik</option>
            <option value="Ayuba">Ayuba</option>
          </select>
        </div>

        <div className="form-group">
          <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ marginBottom: 0 }}>Description</label>
            <div className="responsive-row" style={{ display: 'flex', alignItems: 'center' }}>
              <input
                type="checkbox"
                id="useItemDesc"
                checked={useItemAsDescription}
                onChange={handleUseDescriptionToggle}
                style={{ width: 'auto', marginRight: '6px', cursor: 'pointer' }}
              />
              <label htmlFor="useItemDesc" style={{ marginBottom: 0, fontSize: '13px', fontWeight: 'normal', color: '#666', cursor: 'pointer' }}>
                (Use same as item name)
              </label>
            </div>
          </div>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            required
            placeholder="Enter payment description"
          />
        </div>

        <button
          type="submit"
          className="submit-button"
          disabled={loading || !form.recipient_name}
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </button>
                {error && <div className="error-message" role="alert" style={{ marginTop: '12px' }}>{error}</div>}
      </form>

      {showConfirmation && createPortal(
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px', width: '80%' }}>
            <h2>Confirm Payment</h2>
            <div style={{ textAlign: 'left', margin: '20px 0' }}>
              <p><strong>Amount:</strong> ₦{form.amount}</p>
              <p><strong>Account Name:</strong> {form.recipient_name}</p>
              <p><strong>Bank:</strong> {banks.find(b => b.code === form.bank_code)?.name}</p>
              <p><strong>Description:</strong> {form.description}</p>
            </div>
            <div className="responsive-row" style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button onClick={confirmPayment} style={{ backgroundColor: '#28a745' }}>Confirm Payment</button>
              <button onClick={() => setShowConfirmation(false)} style={{ backgroundColor: '#dc3545' }}>Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Low Balance Modal */}
      {showLowBalanceModal && createPortal(
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '400px', width: '90%', textAlign: 'center', padding: '30px' }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>⚠️</div>
            <h2 style={{ color: '#d32f2f', marginBottom: '15px' }}>Low Balance Alert</h2>
            <p style={{ fontSize: '16px', margin: '10px 0', lineHeight: '1.5', color: '#333' }}>
              Your account balance is running low (<span style={{ fontWeight: 'bold', color: '#d32f2f' }}>₦{balance?.available_balance?.toLocaleString()}</span>).
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>Please top up sufficiently to avoid failed transactions.</p>
            <button
              onClick={() => setShowLowBalanceModal(false)}
              style={{
                marginTop: '25px',
                padding: '10px 30px',
                background: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Verification Countdown Modal */}
      {verificationState.show && createPortal(
        <div className="pin-overlay" style={{ zIndex: 3000 }}>
          <div className="pin-container" style={{ maxWidth: '400px', width: '90%', textAlign: 'center', padding: '30px', position: 'relative' }}>
            <button
              onClick={() => setVerificationState(prev => prev.status === 'SUCCESSFUL' ? { ...prev, show: false } : { ...prev, isCancelled: true })}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999'
              }}
            >
              &times;
            </button>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>{verificationState.status === 'SUCCESSFUL' ? '✅' : '⏳'}</div>
            <h2 style={{ color: '#007bff', marginBottom: '15px' }}>{verificationState.message}</h2>
            <div style={{ fontSize: '42px', fontWeight: 'bold', margin: '20px 0', color: '#333' }}>
              {verificationState.status === 'SUCCESSFUL' ? (
                <div style={{ fontSize: '16px', textAlign: 'left', lineHeight: 1.7 }}>
                  <div><strong>Amount:</strong> ₦{Number(String(verificationState.details?.amount || '').replace(/,/g, '')).toLocaleString()}</div>
                  <div><strong>Recipient:</strong> {verificationState.details?.recipient || '—'}</div>
                  <div><strong>Reference:</strong> {verificationState.details?.reference || '—'}</div>
                  {verificationState.details?.transferId && <div><strong>Transfer ID:</strong> {verificationState.details.transferId}</div>}
                </div>
              ) : `${verificationState.countdown}s`}
            </div>
            <p style={{ color: '#666', fontSize: '14px' }}>{verificationState.status === 'SUCCESSFUL' ? 'Your payment has been verified and recorded.' : 'Please wait, do not close this window.'}</p>
            {verificationState.status === 'SUCCESSFUL' && <button type="button" onClick={() => setVerificationState(prev => ({ ...prev, show: false }))} style={{ marginTop: '12px', padding: '10px 24px', cursor: 'pointer' }}>Close</button>}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function PendingTransfers() {
  const [pendingExpenses, setPendingExpenses] = useState([])
  const [failedExpenses, setFailedExpenses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [verifyingId, setVerifyingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  const fetchPendingTransfers = async () => {
    try {
      setLoading(true)
      const res = await fetch(`${API_BASE}/v1/expenses`)
      if (!res.ok) throw new Error('Failed to fetch expenses')
      const data = await res.json()
      // Pending = transfer initiated but not yet confirmed
      setPendingExpenses(data.filter(exp => (exp.transfer_status === 'PENDING' || exp.transfer_status === 'NEW') && exp.transfer_id))
      // Failed = transfer was verified and came back as FAILED
      setFailedExpenses(data.filter(exp => exp.transfer_status === 'FAILED' || exp.status === 'Failed'))
    } catch (err) {
      setError(err.message || 'Unexpected error')
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchPendingTransfers()
  }, [])

  const handleCheckStatus = async (expense) => {
    try {
      setVerifyingId(expense.id)
      const transferId = parseInt(expense.transfer_id)
      const response = await fetch(`${API_BASE}/flw/transfers/${transferId}`)
      const data = await response.json()

      if (data.status === 'success' && data.data) {
        const transferStatus = data.data.status

        let newStatus = expense.status
        if (transferStatus === 'SUCCESSFUL') {
          newStatus = 'Approved'
        } else if (transferStatus === 'FAILED') {
          newStatus = 'Failed'
        }

        await fetch(`${API_BASE}/v1/expenses/${expense.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: newStatus,
            transfer_status: transferStatus,
            verification_attempts: (expense.verification_attempts || 0) + 1,
            last_verified_at: new Date().toISOString(),
            approved_at: transferStatus === 'SUCCESSFUL' ? new Date().toISOString() : expense.approved_at,
            approved_by: transferStatus === 'SUCCESSFUL' ? 'System (Verified)' : expense.approved_by
          })
        })

        if (transferStatus === 'FAILED') {
          notify(`❌ Transfer FAILED. It has been moved to the "Failed Transfers" section below. You can delete it to clean up your records.`)
        } else {
          notify(`✅ Transfer verified! Status: ${transferStatus}`)
        }
        fetchPendingTransfers()
      } else {
        notify('❌ Failed to verify transfer status')
      }
    } catch (err) {
      notify('Error: ' + err.message)
    } finally {
      setVerifyingId(null)
    }
  }

  const handleDeleteFailed = async (expense) => {
    if (!(await confirmBottom(`Delete failed transfer record for "${expense.item_name}" (₦${Number(expense.amount).toLocaleString()})?\n\nThis will permanently remove it from the database.`))) return
    try {
      setDeletingId(expense.id)
      const res = await fetch(`${API_BASE}/v1/expenses/${expense.id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Delete failed')
      notify('Failed transfer record deleted.', 'success');
            fetchPendingTransfers()
    } catch (err) {
      notify('Error deleting: ' + err.message)
    } finally {
      setDeletingId(null)
    }
  }

  const handleVerifyAll = async () => {
    if (!(await confirmBottom(`Verify all ${pendingExpenses.length} pending transfers?`))) return

    try {
      setVerifying(true)
      const response = await fetch(`${API_BASE}/flw/transfers/verify-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      })

      const data = await response.json()

      if (data.success) {
        const successCount = data.results.filter(r => r.new_status === 'Approved').length
        const failedCount = data.results.filter(r => r.new_status === 'Failed').length
        const stillPending = data.results.filter(r => r.new_status === 'Pending Verification').length

        notify(`✅ Verification complete!\n✓ Successful: ${successCount}\n✗ Failed: ${failedCount}\n⏳ Still Pending: ${stillPending}`)
        fetchPendingTransfers()
      } else {
        notify('❌ Bulk verification failed: ' + data.error)
      }
    } catch (err) {
      notify('Error: ' + err.message)
    } finally {
      setVerifying(false)
    }
  }

  if (loading) return <p>Loading pending transfers...</p>
  if (error) return <p className="error-message">{error}</p>

  return (
    <div>
      {/* ── PENDING SECTION ── */}
      <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Pending Transfers</h1>
        {pendingExpenses.length > 0 && (
          <button
            onClick={handleVerifyAll}
            disabled={verifying}
            style={{
              padding: '10px 20px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: verifying ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {verifying ? '🔄 Verifying All...' : '✓ Verify All Pending'}
          </button>
        )}
      </div>

      {pendingExpenses.length === 0 ? (
        <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
          ✅ No pending transfers to verify
        </p>
      ) : (
        <div className="table-container" tabIndex={0} role="region" aria-label="Scrollable table"><table className="expenses-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Item</th>
              <th>Amount</th>
              <th>Recipient</th>
              <th>Bank</th>
              <th>Transfer Ref</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {pendingExpenses.map((exp) => (
              <tr key={exp.id}>
                <td>{exp.id}</td>
                <td>{new Date(exp.created_at).toLocaleDateString()}</td>
                <td>{exp.item_name}</td>
                <td>₦{Number(exp.amount).toLocaleString()}</td>
                <td>{exp.account_name || exp.paid_to}</td>
                <td>{exp.bank_name}</td>
                <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {exp.transfer_reference}
                </td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '4px',
                    backgroundColor: '#ffc107',
                    color: '#000',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    ⏳ PENDING
                  </span>
                </td>
                <td>{exp.verification_attempts || 0}</td>
                <td>
                  <button
                    onClick={() => handleCheckStatus(exp)}
                    disabled={verifyingId === exp.id}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: verifyingId === exp.id ? 'not-allowed' : 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    {verifyingId === exp.id ? '🔄 Checking...' : '🔍 Check Status'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {/* ── FAILED SECTION ── */}
      {failedExpenses.length > 0 && (
        <div style={{ marginTop: '40px' }}>
          <div className="responsive-row" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '15px' }}>
            <h2 style={{ color: '#dc3545', margin: 0 }}>❌ Failed Transfers</h2>
            <span style={{
              backgroundColor: '#f8d7da',
              color: '#721c24',
              padding: '3px 10px',
              borderRadius: '12px',
              fontSize: '13px',
              fontWeight: '600'
            }}>{failedExpenses.length} record{failedExpenses.length > 1 ? 's' : ''}</span>
          </div>
          <p style={{ color: '#666', fontSize: '13px', marginBottom: '15px' }}>
            These transfers were confirmed as failed by Flutterwave. They are <strong>not counted</strong> in your expenses or financial reports. You can safely delete them to keep your records clean.
          </p>
          <div className="table-container" tabIndex={0} role="region" aria-label="Scrollable table"><table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Item</th>
                <th>Amount</th>
                <th>Recipient</th>
                <th>Bank</th>
                <th>Transfer Ref</th>
                <th>Attempts</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {failedExpenses.map((exp) => (
                <tr key={exp.id} style={{ backgroundColor: '#fff5f5' }}>
                  <td>{new Date(exp.created_at).toLocaleDateString()}</td>
                  <td>{exp.item_name}</td>
                  <td style={{ color: '#dc3545', fontWeight: '600' }}>₦{Number(exp.amount).toLocaleString()}</td>
                  <td>{exp.account_name || exp.paid_to}</td>
                  <td>{exp.bank_name}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: '12px' }}>{exp.transfer_reference}</td>
                  <td>{exp.verification_attempts || 0}</td>
                  <td>
                    <button
                      onClick={() => handleDeleteFailed(exp)}
                      disabled={deletingId === exp.id}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: deletingId === exp.id ? '#aaa' : '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: deletingId === exp.id ? 'not-allowed' : 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      {deletingId === exp.id ? '🗑 Deleting...' : '🗑 Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        </div>
      )}
    </div>
  )
}

// --- Contracts / Governance & Agreement ---

function SignatureModal({ isOpen, onClose, onSave, title }) {
  const sigCanvas = useRef(null)

  if (!isOpen) return null

  const handleSave = () => {
    if (sigCanvas.current.isEmpty()) {
      onSave(null)
    } else {
      onSave(sigCanvas.current.getCanvas().toDataURL('image/png'))
    }
  }

  const clearSig = () => sigCanvas.current.clear()

  return (
    <div className="sig-modal-overlay">
      <div className="sig-modal-content">
        <h3>Sign for: {title}</h3>
        <div className="sig-modal-canvas-wrapper">
          <SignatureCanvas
            ref={sigCanvas}
            penColor='black'
            canvasProps={{ className: 'sigCanvasModal' }}
          />
        </div>
        <div className="sig-modal-actions">
          <button className="contracts-btn-clear" onClick={clearSig}>Clear</button>
          <div className="sig-modal-buttons">
            <button className="contracts-btn-secondary" onClick={onClose}>Cancel</button>
            <button className="contracts-btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function AgreementEditor({ agreement, onBack }) {
  const [content, setContent] = useState(agreement.content || '')
  const [hash, setHash] = useState(agreement.hash || '')
  const [savedContent, setSavedContent] = useState(agreement.content || '')
  const [isSaving, setIsSaving] = useState(false)

  // Signature data states - loaded from agreement if they exist
  const [sig1Data, setSig1Data] = useState(agreement.sig1 || null)
  const [sig2Data, setSig2Data] = useState(agreement.sig2 || null)
  const [activeSigner, setActiveSigner] = useState(null)
  const printRef = useRef(null)

  // Update hash when saved content changes - includes timestamp for versioning
  useEffect(() => {
    const dataToHash = savedContent + (agreement.updated_at || '')
    const newHash = CryptoJS.SHA256(dataToHash).toString(CryptoJS.enc.Hex)
    setHash(newHash)
  }, [savedContent, agreement.updated_at])

  const handlePreview = () => {
    setSavedContent(content)
  }

  const handleSave = async () => {
    const sigCount = (sig1Data ? 1 : 0) + (sig2Data ? 1 : 0)
    const confirmMsg = sigCount > 0
      ? `Are you sure you want to save this version? You are about to finalize an agreement with ${sigCount} signature(s).`
      : "Are you sure you want to save this agreement? This text will be the version tied to future signatures."

    if (!(await confirmBottom(confirmMsg))) return

    setIsSaving(true)
    // Note: hash is updated based on content being saved
    const dataToHash = content + new Date().toISOString()
    const newHash = CryptoJS.SHA256(dataToHash).toString(CryptoJS.enc.Hex)

    try {
      const response = await fetch(`${API_BASE}/v1/agreements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: agreement.id,
          title: agreement.title,
          content,
          hash: newHash,
          sig1: sig1Data,
          sig2: sig2Data
        })
      })
      if (response.ok) {
        setSavedContent(content)
        setHash(newHash)
        const msg = sigCount > 0
          ? `Agreement saved successfully with ${sigCount} signature(s)!`
          : 'Agreement saved successfully!'
        notify(msg)
      } else {
        notify('Failed to save agreement.')
      }
    } catch (error) {
      console.error('Error saving agreement:', error)
      notify('Error connecting to database.')
    } finally {
      setIsSaving(false)
    }
  }

  const generatePDF = () => {
    const element = printRef.current
    const opt = {
      margin: 0.5,
      filename: `${agreement.title.replace(/\s+/g, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: true, windowWidth: 1200 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    }
    html2pdf().set(opt).from(element).save()
  }

  const previewPDF = () => {
    const element = printRef.current
    const opt = {
      margin: 0.5,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: true, windowWidth: 1200 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    }
    html2pdf().set(opt).from(element).toPdf().get('pdf').then(function (pdf) {
      window.open(pdf.output('bloburl'), '_blank')
    })
  }

  const handleEmail = () => {
    const subject = encodeURIComponent(agreement.title)
    const body = encodeURIComponent(`Please find the attached agreement: ${agreement.title}. Note: Attach the downloaded PDF to this email.`)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }

  const handleSignatureSave = (dataUrl) => {
    if (activeSigner === 'abdulmalik') setSig1Data(dataUrl)
    else if (activeSigner === 'ayuba') setSig2Data(dataUrl)
    setActiveSigner(null)
  }

  return (
    <div className="agreement-editor-layout">
      <SignatureModal
        isOpen={!!activeSigner}
        title={activeSigner === 'abdulmalik' ? 'Abdulmalik Badamasi' : 'Ayuba Mako Abdullahi'}
        onClose={() => setActiveSigner(null)}
        onSave={handleSignatureSave}
      />

      <div className="editor-section">
        <button className="contracts-btn-back" onClick={onBack}>&larr; Back to Agreements</button>
        <div className="responsive-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2>Edit Agreement: {agreement.title}</h2>
          {(sig1Data || sig2Data) && content !== savedContent && (
            <span style={{ color: '#dc3545', fontSize: '12px', fontWeight: 'bold' }}>⚠️ Editing will clear signatures on save</span>
          )}
        </div>
        <textarea
          className="agreement-textarea"
          value={content}
          onChange={(e) => {
            const newVal = e.target.value
            setContent(newVal)
            // Automatically clear signatures if text is modified from saved version
            if (newVal !== savedContent) {
              setSig1Data(null)
              setSig2Data(null)
            }
          }}
          placeholder="Enter agreement text here..."
        />
        <div className="editor-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button className="contracts-btn-secondary" onClick={handlePreview} style={{ marginBottom: '0' }}>
            Update Preview
          </button>
          <button className="contracts-btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Agreement'}
          </button>
          <p style={{ fontSize: '11px', color: '#666', width: '100%', marginTop: '8px', fontStyle: 'italic' }}>
            * Both parties must sign this specific version for the agreement to be legally binding.
          </p>
        </div>
      </div>

      <div className="preview-section">
        <div className="preview-toolbar">
          <button className="contracts-btn-secondary" onClick={previewPDF}>Preview PDF</button>
          <button className="contracts-btn-secondary" onClick={generatePDF}>Download PDF</button>
          <button className="contracts-btn-secondary" onClick={handleEmail}>Send via Email</button>
        </div>

        <div className="pdf-preview-container" ref={printRef}>
          <h2 className="pdf-title">{agreement.title}</h2>
          <p style={{ textAlign: 'center', color: '#666', marginBottom: '20px', fontSize: '14px' }}>
            Date: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <div className="pdf-content">
            {savedContent.split(/\n\n+/).map((paragraph, pIdx) => (
              <div key={pIdx} className="pdf-paragraph">
                {paragraph.split('\n').map((line, lIdx) => (
                  <span key={lIdx}>
                    {line.split(/(\b[A-Z]{2,}(?:\s+[A-Z]{2,})*\b)/g).map((part, i) => (
                      /^[A-Z\s]{2,}$/.test(part) ? <strong key={i}>{part}</strong> : part
                    ))}
                    {lIdx < paragraph.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </div>
            ))}
          </div>

          <div className="signatures-container">
            <div className="signature-box" onClick={() => setActiveSigner('abdulmalik')}>
              <h4>Abdulmalik Badamasi</h4>
              <div className="sig-image-wrapper">
                {sig1Data ? (
                  <img src={sig1Data} alt="Signature Abdulmalik" className="sig-image" />
                ) : (
                  <p className="sig-placeholder">Click to Sign</p>
                )}
              </div>
            </div>

            <div className="signature-box" onClick={() => setActiveSigner('ayuba')}>
              <h4>Ayuba Mako Abdullahi</h4>
              <div className="sig-image-wrapper">
                {sig2Data ? (
                  <img src={sig2Data} alt="Signature Ayuba" className="sig-image" />
                ) : (
                  <p className="sig-placeholder">Click to Sign</p>
                )}
              </div>
            </div>
          </div>

          <div className="document-hash">
            <strong>Document Hash:</strong> {hash}
          </div>
        </div>
      </div>
    </div>
  )
}

function Contracts() {
  const [selectedAgreement, setSelectedAgreement] = useState(null)
  const [dbAgreements, setDbAgreements] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [newTitle, setNewTitle] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const defaultAgreements = [
    { id: 'partnership', title: 'Partnership Agreement — True Interactivity Entertainment Network Limited', content: 'PARTNERSHIP AGREEMENT\n\nThis Partnership Agreement ("Agreement") is entered into by and between the undersigned parties in connection with True Interactivity Entertainment Network Limited.\n\nTerms and Conditions:\n1. ...' },
  ]

  const fetchAgreements = async () => {
    try {
      const response = await fetch(`${API_BASE}/v1/agreements`)
      if (response.ok) {
        const data = await response.json()
        setDbAgreements(data)
      }
    } catch (error) {
      console.error('Error fetching agreements:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchAgreements()
  }, [])

  // Merge defaults with DB, and include DB-only agreements
  const mergedDefaults = defaultAgreements.map(def => {
    const dbMatch = dbAgreements.find(db => db.id === def.id)
    return dbMatch ? { ...def, ...dbMatch, title: def.title } : def
  })
  const dbOnlyAgreements = dbAgreements.filter(db => !defaultAgreements.find(def => def.id === db.id))
  const agreements = [...mergedDefaults, ...dbOnlyAgreements]

  const handleCreate = async (e) => {
    e.preventDefault()
    if (!newTitle.trim()) return
    setIsCreating(true)
    const id = 'contract_' + Date.now().toString()
    try {
      const response = await checkedFetch(`${API_BASE}/v1/agreements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id,
          title: newTitle,
          content: `${newTitle.toUpperCase()}\n\nThis agreement is entered into by and between the undersigned parties.\n\nTerms and Conditions:\n1. ...`,
          hash: ''
        })
      })
      if (response.ok) {
        notify('Contract created.', 'success');
        setNewTitle('')
        await fetchAgreements()
      }
    } catch (error) {
      notify('Error creating contract: ' + error.message)
    } finally {
      setIsCreating(false)
    }
  }

  if (!selectedAgreement) {
    return (
      <div className="contracts-container">
        <h1 className="content-title">Governance & Agreement Contracts</h1>
        <p className="contracts-subtitle">Select a contract to view, edit, and sign.</p>

        <form onSubmit={handleCreate} style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
          <input
            type="text"
            placeholder="New Contract Title (e.g., Supplier Agreement)"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            style={{ padding: '10px', flex: 1, borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#fff', color: '#000' }}
          />
          <button
            type="submit"
            disabled={isCreating}
            style={{ padding: '10px 20px', background: '#0070f3', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </button>
        </form>

        {isLoading ? (
          <p>Loading agreements...</p>
        ) : (
          <div className="cards-grid">
            {agreements.map(agr => (
              <div key={agr.id} className="card contracts-card" onClick={() => setSelectedAgreement(agr)}>
                <h2>{agr.title}</h2>
                <p>Click to open editor and sign</p>
                {agr.updated_at && (
                  <small style={{ color: '#888', display: 'block', marginTop: '0.5rem' }}>
                    Last updated: {new Date(agr.updated_at).toLocaleDateString()}
                  </small>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <AgreementEditor
      agreement={selectedAgreement}
      onBack={() => { setSelectedAgreement(null); fetchAgreements(); }}
    />
  )
}

// Modify the App component to include the new routes:
function App() {
  const [selected, setSelected] = useState('dashboard')

  return (
    <div className="app-background">
      <Sidebar selected={selected} setSelected={setSelected} />
      <BottomFeedback />
      <main className="main-content">
        <WalletSyncStatus apiBase={API_BASE} />
        <div key={selected} className="page-transition">
          {selected === 'dashboard' && (
            <>
              <h1>Dashboard</h1>
              <div className="cards-grid">
                <Card title="Make Payment" onClick={() => setSelected('make_payment')}>
                  <p>Transfer funds & auto-log expense</p>
                </Card>
                
                <Card title="Debt" onClick={() => setSelected('debt')}>
                  <p>Track debt and borrowing transactions</p>
                </Card>
                <Card title="Add Expenses" onClick={() => setSelected('add_expenses')}>
                  <p>Submit new expense requests</p>
                </Card>
                <Card title="Approvals" onClick={() => setSelected('approvals')}>
                  <p>Review pending expense requests</p>
                </Card>
                <Card title="Wallet Income" onClick={() => setSelected('income')}>
                  <p>Check incoming funds & credits</p>
                </Card>
                <Card title="Wallet Expense" onClick={() => setSelected('wallet_expense')}>
                  <p>Check outgoing funds & debits</p>
                </Card>
                <Card title="Wallet Ledger" onClick={() => setSelected('incoming_transfers')}>
                  <p>General incoming & outgoing transactions</p>
                </Card>
                <Card title="Reports" onClick={() => setSelected('reports')}>
                  <p>Financial statements and debt manager</p>
                </Card>
                <Card title="Contracts" onClick={() => setSelected('contracts')}>
                  <p>Governance & Agreement Contracts</p>
                </Card>
                <Card title="BOM" onClick={() => setSelected('bom')}>
                  <p>Bill of Materials & Cost Estimator</p>
                </Card>
              </div>
            </>
          )}
          {selected === 'add_expenses' && <AddExpenseForm />}
          {selected === 'expenses' && <ExpensesList />}
          {selected === "debt" && <Debt API_BASE={API_BASE} />}
          {selected === 'make_payment' && <MakePayment />}
          {selected === 'pending_transfers' && <PendingTransfers />}
          {selected === 'income' && <IncomeDetails />}
          {selected === 'wallet_expense' && <WalletExpense />}
          {selected === 'incoming_transfers' && <IncomingTransfers />}
          {selected === 'salary' && <div><h1>Salary</h1><p>Coming soon...</p></div>}
          {selected === 'approvals' && <ApprovalsList />}
          {selected === 'reports' && <FinancialReports API_BASE={API_BASE} />}
          {selected === 'contracts' && <Contracts />}
          {selected === 'bom' && <BOM API_BASE={API_BASE} />}
        </div>
      </main>
    </div>
  )
}

export default App


function WalletExpense() {
  const [activeTab, setActiveTab] = useState("local");

  return (
    <div className="expense-page">
      <h1>Wallet Expense</h1>
      <p style={{ color: '#666', fontSize: '13px', marginBottom: '20px' }}>
        Saved wallet debits include manual records and imported Flutterwave debits. These are separate from business expenses and do not initiate payments.
      </p>

      {/* Tab Switcher */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '0', marginBottom: '25px', borderBottom: '2px solid #eee'
      }}>
        <button
          onClick={() => setActiveTab("local")}
          style={{
            padding: '12px 24px', fontWeight: '600', fontSize: '14px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === 'local' ? '3px solid #28a745' : '3px solid transparent',
            color: activeTab === 'local' ? '#28a745' : '#888'
          }}
        >
          📋 Saved Wallet Debits
        </button>
        <button
          onClick={() => setActiveTab("ledger")}
          style={{
            padding: '12px 24px', fontWeight: '600', fontSize: '14px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === 'ledger' ? '3px solid #0070f3' : '3px solid transparent',
            color: activeTab === 'ledger' ? '#0070f3' : '#888'
          }}
        >
          🏦 Live Flutterwave Debits
        </button>
      </div>

      {activeTab === "local" && <LocalExpenseTab />}
      {activeTab === "ledger" && <FlutterwaveExpenseLedgerTab />}
    </div>
  );
}

// ==================== TAB 1: Local Expense ====================
function LocalExpenseTab() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ amount: '', source: '', category: '', description: '', logged_by: 'System' });
  const [submitting, setSubmitting] = useState(false);

  const fetchExpenses = async () => {
    setLoading(true);
    setError("");
    try {
      const resp = await fetch(`${API_BASE}/v1/wallet-expenses`);
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to load wallet debits');
      setExpenses(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load expenses");
    }
    setLoading(false);
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const resp = await checkedFetch(`${API_BASE}/v1/wallet-expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expense_category: form.category,
          item_name: form.source,
          amount: parseFloat(form.amount),
          description: form.description,
          created_by: form.logged_by,
          currency: "NGN",
        })
      });
      if (!resp.ok) { const data = await resp.json(); throw new Error(data.error || 'Failed to save wallet debit'); }
      notify('Wallet expense saved.', 'success');
      setShowForm(false);
      setForm({ amount: '', source: '', category: '', description: '', logged_by: 'System' });
      fetchExpenses();
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  useEffect(() => {
    fetchExpenses();
    const refresh = () => fetchExpenses();
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, []);

  // Separate local vs synced
  const localEntries = expenses.filter(i => i.origin === 'manual');
  const syncedEntries = expenses.filter(i => i.origin === 'flutterwave');
  const totalExpense = expenses.reduce((sum, i) => sum + Number(i.amount), 0);

  return (
    <div>
      {/* Summary */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '20px', marginBottom: '20px', flexWrap: 'wrap'
      }}>
        <div style={{ padding: '15px 25px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Total Expense (All)</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#16a34a' }}>₦{totalExpense.toLocaleString()}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Manual Entries</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#0284c7' }}>{localEntries.length}</div>
        </div>
        <div style={{ padding: '15px 25px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '8px', minWidth: '180px' }}>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Synced from Flutterwave</div>
          <div style={{ fontSize: '22px', fontWeight: 'bold', color: '#ca8a04' }}>{syncedEntries.length}</div>
        </div>
      </div>

      {/* Add Expense Button & Form */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px', background: showForm ? '#dc3545' : '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
          }}
        >
          {showForm ? '✕ Cancel' : '+ Record Expense'}
        </button>
      </div>

      {showForm && (
        <form className="expense-entry-form" onSubmit={handleAddExpense} style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', padding: '15px',
          background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee', marginBottom: '20px'
        }}>
          <input type="number" placeholder="Amount" value={form.amount}
            onChange={e => setForm({ ...form, amount: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Payee or purpose" value={form.source}
            onChange={e => setForm({ ...form, source: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Category" value={form.category}
            onChange={e => setForm({ ...form, category: e.target.value })} required
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <input type="text" placeholder="Description" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            style={{ padding: '10px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
          <button type="submit" disabled={submitting} style={{
            gridColumn: '1 / -1', padding: '10px', background: '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600'
          }}>
            {submitting ? '⏳ Saving...' : '💾 Save Expense'}
          </button>
        </form>
      )}

      {error && <p className="error-message">{error}</p>}

      {loading ? <p>Loading expense records...</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Payee / Purpose</th>
                <th>Category</th>
                <th>Amount</th>
                <th>Origin</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length > 0 ? expenses.map((inc) => {
                const isSynced = inc.origin === 'flutterwave';
                return (
                  <tr key={inc.id} style={{ backgroundColor: isSynced ? '#fefce8' : '#fafffa' }}>
                    <td>{new Date(inc.created_at || inc.date).toLocaleString()}</td>
                    <td style={{ fontSize: '12px', maxWidth: '250px', wordWrap: 'break-word' }}>{inc.item_name}</td>
                    <td>{inc.expense_category}</td>
                    <td style={{ color: '#dc3545', fontWeight: 'bold' }}>-₦{Number(inc.amount).toLocaleString()}</td>
                    <td>
                      <span style={{
                        background: isSynced ? '#fde68a' : '#d4edda',
                        color: isSynced ? '#92400e' : '#155724',
                        padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold'
                      }}>
                        {isSynced ? '🔄 Flutterwave' : '✍️ Manual'}
                      </span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#555' }}>{inc.description}</td>
                  </tr>
                );
              }) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No expense records found. Click "+ Record Expense" or sync from Live Flutterwave Debits tab.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ==================== TAB 2: Flutterwave Ledger ====================
function FlutterwaveExpenseLedgerTab() {
  const [statement, setStatement] = useState([]);
  const [savedFallback, setSavedFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const fetchExpenses = async (pageNum = 1, append = false) => {
    setLoading(true);
    setError("");
    try {
      const today = new Date().toISOString().split('T')[0];
      const finalEndDate = endDate > today ? today : endDate;
      const url = `${API_BASE}/flw/statement?currency=NGN&from=${startDate}&to=${finalEndDate}&size=50&page=${pageNum}&type=D${append && savedFallback ? '&source=saved' : ''}`;
      const resp = await fetch(url, { cache: 'no-store' });
      const data = await resp.json();

      if (data.status === "success" && data.data) {
        setSavedFallback(data.source === 'saved');
        const credits = data.data.transactions || [];
        if (append && !data.reset) {
          setStatement(prev => [...prev, ...credits]);
        } else {
          setStatement(credits);
        }
        if (data.data.page_info) {
          setHasMore(data.data.page_info.current_page < data.data.page_info.total_pages);
          setPage(data.data.page_info.current_page);
        }
      } else {
        throw new Error(data.message || "Failed to fetch expenses");
      }
    } catch (err) {
      if (append) {
        notify(err.message || 'Could not load more entries. Please retry.', 'error');
      } else {
      setError(err.message || "Unexpected error")
      };
    }
    setLoading(false);
  };

  const loadMore = () => { fetchExpenses(page + 1, true); };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const resp = await fetch(`${API_BASE}/flw/sync-expense`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from: startDate, to: endDate })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || data.message || 'Wallet sync failed');
      setSyncResult(data);
      if (data.success) window.dispatchEvent(new Event('wallet-synced'));
      if (data.success) window.dispatchEvent(new Event('wallet-synced'));
    } catch (err) {
      setSyncResult({ success: false, error: err.message });
    }
    setSyncing(false);
  };

  useEffect(() => { fetchExpenses(); }, []);

  useEffect(() => {
    const refresh = () => fetchExpenses(1, false);
    window.addEventListener('wallet-synced', refresh);
    return () => window.removeEventListener('wallet-synced', refresh);
  }, [startDate, endDate]);

  return (
    <div>
      <p style={{ color: '#555', fontSize: '13px', marginBottom: '15px' }}>
        Live outgoing debits from Flutterwave. Use <strong>Save to App Database</strong> to keep a copy in Saved Wallet Debits. Saving does not initiate a payment or add a business expense to Reports.
      </p>

      {/* Date Filters + Sync Button */}
      <div className="responsive-row" style={{
        display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '15px',
        padding: '15px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #eee',
        flexWrap: 'wrap'
      }}>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <div className="responsive-row" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold' }}>To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            style={{ padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}
          />
        </div>
        <button onClick={() => fetchExpenses(1, false)} disabled={loading}
          style={{
            padding: '10px 20px', background: loading ? '#ccc' : '#28a745', color: 'white',
            border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600'
          }}
        >
          {loading ? '⏳ Updating...' : '🔍 Filter Expense'}
        </button>
        <button onClick={handleSync} disabled={syncing}
          style={{
            padding: '10px 20px', background: syncing ? '#ccc' : '#0070f3', color: 'white',
            border: 'none', borderRadius: '4px', cursor: syncing ? 'not-allowed' : 'pointer', fontWeight: '600'
          }}
        >
          {syncing ? '⏳ Syncing...' : '🔄 Save to App Database'}
        </button>
      </div>

      {/* Sync Result Message */}
      {syncResult && (
        <div style={{
          padding: '12px 15px', marginBottom: '15px', borderRadius: '6px',
          background: syncResult.success ? '#d4edda' : '#f8d7da',
          color: syncResult.success ? '#155724' : '#721c24',
          border: `1px solid ${syncResult.success ? '#c3e6cb' : '#f5c6cb'}`,
          fontSize: '13px'
        }}>
          {syncResult.success
            ? `✅ Sync complete! Fetched ${syncResult.total_fetched} entries — ${syncResult.synced} new synced, ${syncResult.skipped} already existed.`
            : `❌ Sync failed: ${syncResult.error}`
          }
        </div>
      )}

      <WalletLedgerNotice visible={savedFallback} />
      {loading && statement.length === 0 ? <p>Loading expense entries...</p> : error ? <p className="error-message">{error}</p> : (
        <div className="table-container">
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Balance After</th>
                <th>Description / Remarks</th>
              </tr>
            </thead>
            <tbody>
              {statement.length > 0 ? (
                <>
                  {statement.map((entry, idx) => (
                    <tr key={idx} style={{ backgroundColor: '#fafffa' }}>
                      <td>{new Date(entry.date).toLocaleString()}</td>
                      <td style={{ fontSize: '11px', fontFamily: 'monospace' }}>{entry.reference}</td>
                      <td><span style={{ background: '#f8d7da', color: '#721c24', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 'bold' }}>DEBIT</span></td>
                      <td style={{ color: '#dc3545', fontWeight: 'bold' }}>-₦{Number(entry.amount).toLocaleString()}</td>
                      <td style={{ fontWeight: '500' }}>{entry.balance_after == null ? '—' : `₦${Number(entry.balance_after).toLocaleString()}`}</td>
                      <td style={{ fontSize: '12px', color: '#555' }}>{entry.remarks}</td>
                    </tr>
                  ))}
                  {hasMore && (
                    <tr>
                      <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                        <button onClick={loadMore} disabled={loading}
                          style={{ padding: '10px 30px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '600' }}
                        >
                          {loading ? 'Loading...' : '⬇️ Load More Entries'}
                        </button>
                      </td>
                    </tr>
                  )}
                </>
              ) : (
                <tr><td colSpan="6" style={{ textAlign: 'center', padding: '40px' }}>No expense entries found for this date range.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
